import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../../services/api';
import { portfolioReportsAPI, portfolioReportsBulkAPI } from '../../services/api';
import ModalShell from '../../components/ModalShell';
import OffscreenReportRenderer from './OffscreenReportRenderer';

/**
 * Bulk download modal for quarterly reports.
 *
 * WYSIWYG flow:
 * 1. User selects portfolios
 * 2. For each: fetch report data → render offscreen via ReportViewer → capture DOM HTML
 * 3. Send all HTML captures to backend WYSIWYG bulk endpoint
 * 4. Backend renders each via Playwright, zips, returns ZIP blob
 * 5. Download
 */
const BulkDownloadModal = ({
  isOpen,
  onClose,
  isDrummondUser,
  quarter,
  perfDate,
  holdingsDate,
  changesBaseDate,
  saaYear,
  dateOverrides = {},  // per-portfolio date exceptions: {portfolio_code: {perf_date, holdings_date, changes_base_date}}
}) => {
  const [selected, setSelected] = useState(new Set());
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Lazy loading state for brand groups
  const [collapsedGroups, setCollapsedGroups] = useState({});
  const [loadingBrands, setLoadingBrands] = useState({});

  // Generation state
  const [isGenerating, setIsGenerating] = useState(false);
  const [genPhase, setGenPhase] = useState(''); // 'capturing' | 'rendering' | ''
  const [genProgress, setGenProgress] = useState(0);
  const [genTotal, setGenTotal] = useState(0);
  const [genMessage, setGenMessage] = useState(null);

  // Offscreen rendering state — render one portfolio at a time
  const [currentRenderData, setCurrentRenderData] = useState(null);
  const capturedItemsRef = useRef([]);
  const renderQueueRef = useRef([]);
  const abortRef = useRef(false);

  // Fetch portfolio data when modal opens
  useEffect(() => {
    if (!isOpen) return;
    setSelected(new Set());
    setError(null);
    setGenMessage(null);
    setIsGenerating(false);
    setGenPhase('');
    fetchPortfolios();
  }, [isOpen]);

  const fetchPortfolios = async () => {
    setLoading(true);
    try {
      if (isDrummondUser) {
        // Only fetch Drummond portfolios + brand metadata (no brand portfolio lists)
        const [portfoliosRes, brandsRes] = await Promise.all([
          api.get('/api/portfolio-reports/portfolios'),
          api.get('/api/portfolio-reports/portfolios/brands'),
        ]);

        const drummondPortfolios = portfoliosRes.data.portfolios || [];
        const brands = brandsRes.data.brands || [];

        setGroups([
          {
            label: 'DRUMMOND PORTFOLIOS',
            domain: null,
            portfolios: drummondPortfolios.map(p => ({ code: p.portfolio_code, name: p.name })),
          },
          // Brand groups start with empty portfolios — loaded lazily on expand
          ...brands.map(b => ({
            label: b.company_name || b.domain,
            domain: b.domain,
            portfolios: [],
          })),
        ]);
        // Drummond group starts expanded, brands collapsed
        const initialCollapsed = {};
        brands.forEach(b => { initialCollapsed[b.domain] = true; });
        setCollapsedGroups(initialCollapsed);
      } else {
        const res = await api.get('/api/portfolio-reports/portfolios');
        const portfolios = res.data.portfolios || [];
        setGroups([{
          label: 'PORTFOLIOS',
          domain: null,
          portfolios: portfolios.map(p => ({ code: p.portfolio_code, name: p.name })),
        }]);
      }
    } catch (err) {
      console.error('Error fetching portfolios for bulk download:', err);
      setError('Failed to load portfolios');
    } finally {
      setLoading(false);
    }
  };

  // Lazy-load brand portfolios on group expand. Returns loaded portfolios array.
  const loadBrandPortfolios = async (domain) => {
    setLoadingBrands(prev => ({ ...prev, [domain]: true }));
    try {
      const res = await api.get(`/api/portfolio-reports/portfolios/brands/${encodeURIComponent(domain)}`);
      const portfolios = (res.data.portfolios || []).map(p => ({ code: p.portfolio_code, name: p.name }));
      setGroups(prev => prev.map(g =>
        g.domain === domain ? { ...g, portfolios } : g
      ));
      return portfolios;
    } catch (err) {
      console.warn(`Failed to load portfolios for ${domain}:`, err);
      setGroups(prev => prev.map(g =>
        g.domain === domain ? { ...g, portfolios: [] } : g
      ));
      return [];
    } finally {
      setLoadingBrands(prev => ({ ...prev, [domain]: false }));
    }
  };

  const toggleGroup = (domain) => {
    if (!domain) return; // Drummond group always expanded
    const wasCollapsed = collapsedGroups[domain];
    setCollapsedGroups(prev => ({ ...prev, [domain]: !wasCollapsed }));

    // Lazy-load on first expand
    if (wasCollapsed) {
      const group = groups.find(g => g.domain === domain);
      if (group && group.portfolios.length === 0 && !loadingBrands[domain]) {
        loadBrandPortfolios(domain);
      }
    }
  };

  const makeKey = (code, domain) => domain ? `${code}|||${domain}` : code;
  const parseKey = (key) => {
    const parts = key.split('|||');
    return { code: parts[0], domain: parts[1] || null };
  };

  const toggleItem = (key) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const selectGroup = (group, checked) => {
    setSelected(prev => {
      const next = new Set(prev);
      group.portfolios.forEach(p => {
        const key = makeKey(p.code, group.domain);
        if (checked) next.add(key);
        else next.delete(key);
      });
      return next;
    });
  };

  const isGroupAllSelected = (group) =>
    group.portfolios.length > 0 &&
    group.portfolios.every(p => selected.has(makeKey(p.code, group.domain)));

  const isGroupPartial = (group) => {
    const count = group.portfolios.filter(p => selected.has(makeKey(p.code, group.domain))).length;
    return count > 0 && count < group.portfolios.length;
  };

  const selectAll = async () => {
    // Load all unloaded brand groups first
    const unloaded = groups.filter(g => g.domain && g.portfolios.length === 0 && !loadingBrands[g.domain]);
    if (unloaded.length > 0) {
      await Promise.all(unloaded.map(g => loadBrandPortfolios(g.domain)));
    }
    // Expand all groups
    setCollapsedGroups({});
    // Select will be applied after groups state updates via effect below
    setShouldSelectAll(true);
  };

  // Deferred select-all after brand loads complete
  const [shouldSelectAll, setShouldSelectAll] = useState(false);
  useEffect(() => {
    if (!shouldSelectAll) return;
    const all = new Set();
    groups.forEach(g => g.portfolios.forEach(p => all.add(makeKey(p.code, g.domain))));
    setSelected(all);
    setShouldSelectAll(false);
  }, [shouldSelectAll, groups]);

  const selectNone = () => setSelected(new Set());

  const selectDrummondOnly = () => {
    const drummond = new Set();
    const dGroup = groups.find(g => g.domain === null);
    if (dGroup) dGroup.portfolios.forEach(p => drummond.add(makeKey(p.code, null)));
    setSelected(drummond);
  };

  const selectBrand = async (domain) => {
    // Ensure brand is loaded
    const group = groups.find(g => g.domain === domain);
    if (group && group.portfolios.length === 0 && !loadingBrands[domain]) {
      await loadBrandPortfolios(domain);
    }
    // Expand the group
    setCollapsedGroups(prev => ({ ...prev, [domain]: false }));
    // Select will be applied after groups state updates
    setShouldSelectBrand(domain);
  };

  const [shouldSelectBrand, setShouldSelectBrand] = useState(null);
  useEffect(() => {
    if (!shouldSelectBrand) return;
    const brand = new Set();
    const bGroup = groups.find(g => g.domain === shouldSelectBrand);
    if (bGroup) bGroup.portfolios.forEach(p => brand.add(makeKey(p.code, shouldSelectBrand)));
    setSelected(brand);
    setShouldSelectBrand(null);
  }, [shouldSelectBrand, groups]);

  // Find portfolio name from groups
  const getPortfolioName = (code) => {
    for (const g of groups) {
      const p = g.portfolios.find(p => p.code === code);
      if (p) return p.name;
    }
    return code;
  };

  // Called by OffscreenReportRenderer when DOM capture is ready
  const handleCapture = useCallback((html) => {
    const queue = renderQueueRef.current;
    const current = queue[0];
    if (!current) return;

    const name = getPortfolioName(current.code);
    const safeName = name.replace(/\s+/g, '_').replace(/\//g, '_');
    const prefix = current.domain ? `${current.domain}_` : '';
    const quarterLabel = (quarter || '').replace('-', '_');
    const filename = `${prefix}${quarterLabel}_${safeName}_Quarterly_Report.pdf`;

    capturedItemsRef.current.push({ html_content: html, filename });

    // Move to next in queue
    queue.shift();
    setGenProgress(prev => prev + 1);

    if (abortRef.current || queue.length === 0) {
      // Done capturing — send to backend
      setCurrentRenderData(null);
      submitToBackend();
    } else {
      // Load next portfolio
      loadNextPortfolio();
    }
  }, [quarter, groups]);

  const loadNextPortfolio = async () => {
    const queue = renderQueueRef.current;
    if (queue.length === 0) return;

    const item = queue[0];
    try {
      // A per-portfolio date exception takes precedence over the canonical quarter dates
      const override = dateOverrides[item.code] || {};
      const result = await portfolioReportsAPI.getReportData(
        item.code, quarter, item.domain,
        {
          perfDate: override.perf_date || perfDate,
          holdingsDate: override.holdings_date || holdingsDate,
          changesBaseDate: override.changes_base_date || changesBaseDate,
          // Same SAA vintage as the preview/single download — omitting this made
          // bulk reports derive the vintage from the period, so tilts (e.g.
          // Global Alpha) diverged whenever the quarter has an saa_year override.
          saaYear: override.saa_year || saaYear,
        }
      );
      if (result.success) {
        setCurrentRenderData(result.data);
      } else {
        // Skip this one
        console.warn(`Failed to load data for ${item.code}: ${result.message}`);
        queue.shift();
        setGenProgress(prev => prev + 1);
        if (queue.length > 0) {
          loadNextPortfolio();
        } else {
          setCurrentRenderData(null);
          submitToBackend();
        }
      }
    } catch (err) {
      console.warn(`Failed to load data for ${item.code}:`, err);
      queue.shift();
      setGenProgress(prev => prev + 1);
      if (queue.length > 0) {
        loadNextPortfolio();
      } else {
        setCurrentRenderData(null);
        submitToBackend();
      }
    }
  };

  const submitToBackend = async () => {
    const items = capturedItemsRef.current;
    if (items.length === 0) {
      setIsGenerating(false);
      setGenPhase('');
      setGenMessage({ type: 'error', text: 'No reports could be captured. Check that report data is available.' });
      return;
    }

    setGenPhase('rendering');
    setGenMessage({ type: 'info', text: `Rendering ${items.length} PDFs on server...` });

    try {
      const response = await portfolioReportsBulkAPI.generateWysiwyg(items);

      // Download the blob
      const blob = new Blob([response.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const quarterLabel = (quarter || '').replace('-', '_');
      link.setAttribute('download', `quarterly_reports_${quarterLabel}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      const generated = parseInt(response.headers?.['x-reports-generated'] || items.length, 10);
      const failed = parseInt(response.headers?.['x-reports-failed'] || '0', 10);

      setGenMessage({
        type: failed > 0 ? 'warning' : 'success',
        text: failed > 0
          ? `Generated ${generated} reports. ${failed} failed.`
          : `Successfully generated ${generated} reports!`,
      });
    } catch (err) {
      console.error('Error generating bulk PDFs:', err);
      setGenMessage({ type: 'error', text: 'Failed to generate PDFs on server.' });
    } finally {
      setIsGenerating(false);
      setGenPhase('');
    }
  };

  const handleGenerate = () => {
    const items = Array.from(selected).map(key => parseKey(key));
    if (items.length === 0) return;

    setIsGenerating(true);
    setGenPhase('capturing');
    setGenProgress(0);
    setGenTotal(items.length);
    setGenMessage({ type: 'info', text: `Capturing ${items.length} reports...` });
    capturedItemsRef.current = [];
    renderQueueRef.current = [...items];
    abortRef.current = false;

    loadNextPortfolio();
  };

  const loadedPortfolios = groups.reduce((sum, g) => sum + g.portfolios.length, 0);
  const guardedClose = isGenerating ? undefined : onClose;
  const progressPct = genTotal > 0 ? Math.round((genProgress / genTotal) * 100) : 0;

  return (
    <>
      <ModalShell
        isOpen={isOpen}
        onClose={guardedClose}
        title="Bulk Download Quarterly Reports"
        maxWidth="700px"
        initialWidth="700px"
        initialHeight="80vh"
      >
        {/* Body */}
        <div className="bulk-modal-body" style={{ padding: '16px 20px', flex: 1, overflowY: 'auto' }}>
          {loading && (
            <div className="text-center py-4">
              <div className="spinner-border spinner-border-sm text-primary" role="status" />
              <p className="mt-2 text-muted mb-0" style={{ fontSize: '0.875rem' }}>Loading portfolios...</p>
            </div>
          )}

          {error && (
            <div className="alert alert-danger py-2" style={{ fontSize: '0.875rem' }}>
              <i className="bi bi-exclamation-triangle me-1"></i>{error}
            </div>
          )}

          {!loading && !error && (
            <>
              {isDrummondUser && (
                <div className="bulk-cascade-bar">
                  <button className="btn btn-sm btn-outline-primary" onClick={selectAll} disabled={isGenerating}>
                    Select All
                  </button>
                  <button className="btn btn-sm btn-outline-secondary" onClick={selectNone} disabled={isGenerating}>
                    Clear
                  </button>
                  <button className="btn btn-sm btn-outline-primary" onClick={selectDrummondOnly} disabled={isGenerating}>
                    Drummond Only
                  </button>
                  {groups.filter(g => g.domain !== null).map(g => (
                    <button
                      key={g.domain}
                      className="btn btn-sm btn-outline-primary"
                      onClick={() => selectBrand(g.domain)}
                      disabled={isGenerating}
                    >
                      {g.label}
                    </button>
                  ))}
                </div>
              )}

              {groups.map((group) => {
                const isDrummondGroup = group.domain === null;
                const isCollapsed = !isDrummondGroup && collapsedGroups[group.domain];
                const isLoadingDomain = group.domain && loadingBrands[group.domain];
                const isExpanded = isDrummondGroup || !isCollapsed;

                return (
                  <div key={group.domain || '_drummond'} style={{ marginBottom: '0.75rem' }}>
                    <div className="bulk-group-header" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {!isDrummondGroup && (
                        <i
                          className={`bi bi-chevron-${isExpanded ? 'down' : 'right'}`}
                          style={{ cursor: 'pointer', fontSize: '0.8rem', color: '#6c757d' }}
                          onClick={() => toggleGroup(group.domain)}
                        />
                      )}
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: 0, cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          className="form-check-input"
                          checked={isGroupAllSelected(group)}
                          ref={input => {
                            if (input) input.indeterminate = isGroupPartial(group);
                          }}
                          onChange={(e) => {
                            // If brand not loaded yet, load first then select all
                            if (!isDrummondGroup && group.portfolios.length === 0 && e.target.checked) {
                              loadBrandPortfolios(group.domain).then((loaded) => {
                                setCollapsedGroups(prev => ({ ...prev, [group.domain]: false }));
                                if (loaded.length > 0) {
                                  setSelected(prev => {
                                    const next = new Set(prev);
                                    loaded.forEach(p => next.add(makeKey(p.code, group.domain)));
                                    return next;
                                  });
                                }
                              });
                              return;
                            }
                            selectGroup(group, e.target.checked);
                          }}
                          disabled={isGenerating}
                        />
                        <span
                          onClick={(e) => { if (!isDrummondGroup) { e.preventDefault(); toggleGroup(group.domain); } }}
                          style={{ cursor: isDrummondGroup ? 'default' : 'pointer' }}
                        >
                          {group.label} {group.portfolios.length > 0 ? `(${group.portfolios.length})` : ''}
                        </span>
                      </label>
                    </div>

                    {isExpanded && isLoadingDomain && (
                      <div style={{ paddingLeft: '2rem', paddingTop: '4px' }}>
                        <span className="spinner-border spinner-border-sm text-primary" role="status" />
                        <span className="text-muted ms-2" style={{ fontSize: '0.8rem' }}>Loading...</span>
                      </div>
                    )}

                    {isExpanded && !isLoadingDomain && group.portfolios.map(p => {
                      const key = makeKey(p.code, group.domain);
                      return (
                        <label key={key} className="bulk-portfolio-item">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            checked={selected.has(key)}
                            onChange={() => toggleItem(key)}
                            disabled={isGenerating}
                          />
                          {p.name}
                        </label>
                      );
                    })}
                  </div>
                );
              })}
            </>
          )}

          {isGenerating && (
            <div style={{ marginTop: '1rem' }}>
              <div className="d-flex justify-content-between mb-1">
                <small className="text-muted">
                  {genPhase === 'capturing' && `Capturing report ${genProgress + 1} of ${genTotal}...`}
                  {genPhase === 'rendering' && 'Rendering PDFs on server...'}
                </small>
                <small className="text-muted">
                  {genPhase === 'capturing' ? `${progressPct}%` : ''}
                </small>
              </div>
              <div className="progress" style={{ height: '8px' }}>
                <div
                  className="progress-bar progress-bar-striped progress-bar-animated"
                  role="progressbar"
                  style={{
                    width: genPhase === 'rendering' ? '100%' : `${progressPct}%`,
                    background: '#072040',
                  }}
                />
              </div>
            </div>
          )}

          {genMessage && (
            <div
              className={`alert alert-${genMessage.type === 'error' ? 'danger' : genMessage.type === 'warning' ? 'warning' : genMessage.type === 'info' ? 'info' : 'success'} py-2 mt-3`}
              style={{ fontSize: '0.85rem' }}
            >
              {genMessage.text}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bulk-modal-footer" style={{
          padding: '12px 20px',
          borderTop: '1px solid #e0e0e0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexShrink: 0,
        }}>
          <span style={{ fontSize: '0.85rem', color: '#6c757d' }}>
            {selected.size} selected{loadedPortfolios > 0 ? ` of ${loadedPortfolios} loaded` : ''}
          </span>
          <div className="d-flex gap-2">
            <button
              className="btn btn-outline-secondary"
              onClick={onClose}
              disabled={isGenerating}
            >
              Cancel
            </button>
            <button
              className="btn btn-primary"
              onClick={handleGenerate}
              disabled={isGenerating || selected.size === 0}
              style={{ background: '#072040', borderColor: '#072040' }}
            >
              {isGenerating ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                  {genPhase === 'capturing' ? `Capturing... ${progressPct}%` : 'Rendering...'}
                </>
              ) : (
                <>
                  <i className="bi bi-download me-2"></i>
                  Download ({selected.size})
                </>
              )}
            </button>
          </div>
        </div>
      </ModalShell>

      {/* Offscreen renderer — renders one portfolio at a time for DOM capture */}
      {currentRenderData && (
        <OffscreenReportRenderer
          data={currentRenderData}
          onCapture={handleCapture}
        />
      )}
    </>
  );
};

export default BulkDownloadModal;
